import { createRouter, createMemoryHistory } from 'vue-router';
import Log from '@/Utils/Log';
import { displayModeValues } from '@/Plugins/DisplayModes';

const location = window.location;
const history = window.history;
const hash = location.hash;

// Catch-all fallback route — Router is installed via app.use(Router) for every
// Vue component on a page (Core.js), but most pages don't call App.Router.addRoute().
// Without a matching route, Vue Router 4 emits "No match found for location with path".
const routes = [{
    path: '/:pathMatch(.*)*',
    meta: { noScroll: true },
    component: { render: () => null },
}];
const savedScrollPositions = {};

// Redirect to admin page if hash starts with #/admin
if (hash.startsWith('#/admin')) {
    location.href = hash.replace('#', '');
}

const router = createRouter({
    history: createMemoryHistory(),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (to.matched.some((record) => record.meta.noScroll) || to.query.focusFields) {
            return null;
        }
        else if (savedPosition) {
            return savedPosition;
        }
        else {
            return { left: 0, top: 0 };
        }
    },
});

router.previousRoute = null;
router.previousRouteReplaced = false;

function getCurrentHashPath() {
    const path = location.hash.replace('#', '');
    return path.length > 0 ? path : null;
}

function registerHashWatchToRouter(router) {
    if (location.hash) {
        router.push(getCurrentHashPath()).catch(() => {});
    }

    window.addEventListener('hashchange', handleHashChange(router), false);
    window.addEventListener('popstate', handlePopstate(router), false);
}

function handleHashChange(router) {
    return () => {
        const { isWeb, isPWA } = displayModeValues;
        const hashPath = getCurrentHashPath();
        const routerPath = router.currentRoute.value.fullPath;
        const isHashPathEmpty = hashPath !== routerPath && history.state === null;

        if (isHashPathEmpty && isWeb) {
            router.push(hashPath ? hashPath : '/').catch(() => {});
        }
        else if (isHashPathEmpty && isPWA) {
            router.replace(hashPath ? hashPath : '/').catch(() => {});
        }
    };
}

function handlePopstate(router) {
    return () => {
        const { isWeb } = displayModeValues;
        const hashPath = getCurrentHashPath();
        const routerPath = router.currentRoute.value.fullPath;
        if (hashPath !== routerPath && history.state !== null && isWeb) {
            router.replace(hashPath ? hashPath : '/').catch(() => {});
        }
    };
}

router.beforeEach((to, from) => {
    if (savedScrollPositions[to.fullPath] && from?.meta && !from.meta.noScroll) {
        window.scrollTo(0, savedScrollPositions[to.fullPath].y);
    }
    router.previousRoute = from;
});

function saveScrollPosition(from) {
    if (from?.meta?.noScroll !== true && from?.name) {
        savedScrollPositions[from.fullPath] = { y: window.pageYOffset };
    }
}

router.afterEach((to, from) => {
    const { isWeb } = displayModeValues;
    saveScrollPosition(from);

    if (to && isWeb) {
        const hashPath = getCurrentHashPath();
        const pathnameWithSearch = `${location.pathname}${location.search}`;
        const fromFullPath = from?.fullPath;
        const toFullPath = to?.fullPath;

        if (hashPath !== toFullPath) {
            const state = { from: fromFullPath, to: toFullPath };
            const isInitialNavigation = !from?.matched?.length;

            if (to.name === null || to.name === undefined) {
                if (isInitialNavigation) {
                    history.replaceState(state, '', pathnameWithSearch);
                }
                else {
                    history.pushState(state, '', pathnameWithSearch);
                }
            }
            else {
                const fullPathWithHash = `${pathnameWithSearch}#${toFullPath}`;
                const params = [state, '', fullPathWithHash];

                if (router.previousRouteReplaced || isInitialNavigation) {
                    history.replaceState(...params);
                }
                else {
                    history.pushState(...params);
                }
            }

            router.previousRouteReplaced = false;
        }
    }
});

const originalResolve = router.resolve.bind(router);
const originalReplace = router.replace.bind(router);

// Prefix resolved href with '#' to keep memory router URLs hash-based in the browser.
router.resolve = function (...args) {
    try {
        const originalResult = originalResolve(...args);
        originalResult.href = `#${originalResult.href}`;
        return originalResult;
    }
    catch (error) {
        Log.error(`Cannot resolve router link. Details: ${args}`);
        throw error;
    }
};

router.replace = function (...args) {
    router.previousRouteReplaced = true;
    return originalReplace(...args);
};

registerHashWatchToRouter(router);

export default router;
